<!-- Link.vue -->
<template>
  <div class="link-wrapper">
    <!-- Link button to open the dialog -->
    <button @click="openLinkDialog" :class="{ 'is-active': isLinkActive }"
      class="border rounded px-2 flex items-center h-8 cursor-pointer hover:bg-gray-100 bg-white"
      title="Add or Edit Link">
      <Icon name="lucide:link" />
    </button>

    <!-- Dialog for entering/editing the link URL -->
    <Dialog v-model:visible="showLinkDialog" header="Add or Edit Link" :modal="true" :style="{ width: '400px' }"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }">
      <div class="p-field">
        <label for="link-url" class="block mb-2">URL</label>
        <InputText id="link-url" v-model="linkUrl" type="text" placeholder="Enter URL (e.g., https://example.com)"
          class="w-full" @keyup.enter="applyLink" @keyup.esc="closeLinkDialog" ref="linkInput" />
      </div>
      <template #footer>
        <Button label="Cancel" class="p-button-text" @click="closeLinkDialog" />
        <Button v-if="isLinkActive" label="Remove" class="p-button-danger p-button-text" @click="removeLink" />
        <Button label="Apply" @click="applyLink" :disabled="!linkUrl.trim()" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { computed, defineModel, nextTick, ref } from 'vue'

const editor = defineModel('editor')

const showLinkDialog = ref(false)
const linkUrl = ref('')
const linkInput = ref<HTMLInputElement | null>(null)

const isLinkActive = computed(() => {
  return editor.value?.isActive('link') || false
})

const currentLinkUrl = computed(() => {
  return editor.value?.getAttributes('link').href || ''
})

const openLinkDialog = () => {
  showLinkDialog.value = true
  linkUrl.value = currentLinkUrl.value
  nextTick(() => {
    linkInput.value?.focus()
  })
}

const applyLink = () => {
  if (linkUrl.value.trim() === '') {
    removeLink()
  } else {
    // Validate and format the URL
    let url = linkUrl.value.trim()
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`
    }
    editor.value?.chain().focus().setLink({ href: url }).run()
  }
  closeLinkDialog()
}

// Remove the link from the selected text
const removeLink = () => {
  editor.value?.chain().focus().unsetLink().run()
  closeLinkDialog()
}

const closeLinkDialog = () => {
  showLinkDialog.value = false
  linkUrl.value = ''
}
</script>

<style scoped>
.link-wrapper {
  display: inline-block;
}

.border.is-active {
  background: #d0eaff !important;
  border-color: #007bff !important;
}

:deep(.editor-link) {
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
}

:deep(.editor-link:hover) {
  color: #0056b3;
}
</style>